import { useEffect, useRef, useState } from "react";
import { Siren, PhoneCall, Check, X, Share2, Printer } from "lucide-react";
import { api } from "../../api";

const ROUTE_LABEL = {
  skin: "Skin contact",
  eye: "Eye contact",
  inhale: "Inhalation",
  ingest: "Ingestion",
};

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function EmergencyMode({ open, onClose, chemKey, chemName, route }) {
  const [data, setData] = useState(null);
  const [durationSec, setDurationSec] = useState(20 * 60);
  const [remaining, setRemaining] = useState(20 * 60);
  const [stepIdx, setStepIdx] = useState(0);
  const [checked, setChecked] = useState({});
  const [running, setRunning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setChecked({});
    setRunning(false);
    Promise.all([
      api.getFirstAid(chemKey, route),
      api.getFlushDuration(chemKey, route).catch(() => ({ duration_sec: 20 * 60 })),
    ]).then(([aid, dur]) => {
      setData(aid);
      setDurationSec(dur.duration_sec || 20 * 60);
      setRemaining(dur.duration_sec || 20 * 60);
    });
  }, [open, chemKey, route]);

  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  if (!open) return null;

  const step = data?.steps?.[stepIdx];
  const totalSteps = data?.steps?.length || 0;
  const done = stepIdx >= totalSteps - 1;
  const flushPct = 100 - Math.round((remaining / durationSec) * 100);

  function next() {
    setChecked((c) => ({ ...c, [stepIdx]: true }));
    if (!done) setStepIdx((i) => i + 1);
  }

  function shareWhatsApp() {
    const lines = [
      `EMERGENCY — ${chemName} (${ROUTE_LABEL[route] || route})`,
      "",
      "Steps:",
      ...(data?.steps || []).map((s, i) => `${i + 1}. ${s}`),
      "",
      "DO NOT:",
      ...(data?.not || []).map((s) => `- ${s}`),
      "",
      "Poison Control (IN): 1800-11-6117",
    ].join("\n");
    const url = `https://wa.me/?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank");
  }

  function printCard() {
    window.print();
  }

  return (
    <div className="emergency-backdrop" data-testid="emergency-mode">
      <div className="emergency-shell">
        <div className="emergency-top">
          <div className="emergency-title">
            <Siren size={22} />
            <div>
              <div className="eyebrow" style={{ color: "#ffb0a5", marginBottom: 4 }}>Emergency mode</div>
              <h2 style={{ color: "#fff", fontSize: 22 }}>{chemName} — {ROUTE_LABEL[route] || route}</h2>
            </div>
          </div>
          <button className="emergency-close" onClick={onClose} aria-label="Exit" data-testid="emergency-close">
            <X size={20} />
          </button>
        </div>

        <div className="emergency-timer-row">
          <div className="emergency-timer" data-testid="emergency-timer">
            <div className="timer-label">Flush time remaining</div>
            <div className="timer-value mono">{fmt(remaining)}</div>
            <div className="timer-bar"><div className="timer-fill" style={{ width: `${flushPct}%` }} /></div>
          </div>
          <div className="emergency-actions">
            {!running ? (
              <button
                className="btn btn-emergency-start"
                onClick={() => setRunning(true)}
                data-testid="emergency-start-timer"
              >
                Start flush timer
              </button>
            ) : (
              <button className="btn btn-ghost-white" onClick={() => setRunning(false)} data-testid="emergency-pause-timer">
                Pause timer
              </button>
            )}
            <a href="tel:18001116117" className="btn btn-emergency-call" data-testid="emergency-call">
              <PhoneCall size={16} /> 1800-11-6117
            </a>
          </div>
        </div>

        <div className="emergency-body">
          <div className="emergency-step-box">
            <div className="step-count">
              Step <span className="mono">{stepIdx + 1}</span> of <span className="mono">{totalSteps || "…"}</span>
            </div>
            <p className="emergency-step-text" data-testid="emergency-step-text">
              {step || "Loading steps…"}
            </p>
            <div className="emergency-nav">
              <button
                className="btn btn-ghost-white"
                disabled={stepIdx === 0}
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                data-testid="emergency-prev"
              >
                Back
              </button>
              <button className="btn btn-emergency-next" onClick={next} data-testid="emergency-next" disabled={done && checked[stepIdx]}>
                {done ? (checked[stepIdx] ? "All steps completed" : "Mark last step done") : "Next step"}
                {!done && <Check size={16} />}
              </button>
            </div>
            <ul className="emergency-checklist" data-testid="emergency-checklist">
              {data?.steps?.map((s, i) => (
                <li key={i} className={i === stepIdx ? "active" : ""}>
                  <span className={"tick " + (checked[i] ? "on" : "")}>
                    {checked[i] ? <Check size={13} /> : i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="emergency-donot">
            <h4>DO NOT</h4>
            <ul>{data?.not?.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        </div>

        <div className="emergency-footer">
          <button className="btn btn-ghost-white" onClick={shareWhatsApp} data-testid="emergency-share-whatsapp">
            <Share2 size={16} /> Share to WhatsApp
          </button>
          <button className="btn btn-ghost-white" onClick={printCard} data-testid="emergency-print">
            <Printer size={16} /> Print card
          </button>
        </div>
      </div>
    </div>
  );
}
